import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import envConfig from '../../../env-config';

// Import mock data generators
import { getMockRequests } from '../../mocks/requests.mock';
import { getMockRequestDetail } from '../../mocks/request-details.mock';
import {
  getMockCompanyByArNumber,
  searchMockCompanies,
  getPaginatedMockCompanies,
} from '../../mocks/companies.mock';
import { getMockUsers } from '../../mocks/users.mock';
import { getMockPrintSites } from '../../mocks/print-sites.mock';
import { getMockCheckLayouts } from '../../mocks/check-layouts.mock';

/**
 * Mock HTTP Interceptor
 *
 * Intercepts HTTP requests and returns mock data when useMockData is enabled.
 * This allows development without a backend.
 *
 * To disable: Set useMockData: false in env-config/index.build-dev.ts
 * To connect to backend: Set apiUrl and useMockData: false
 */
@Injectable()
export class MockInterceptor implements HttpInterceptor {
  private readonly mockDelay = 300; // Simulate network delay (ms)

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Only intercept if mock mode is enabled
    if (!envConfig.useMockData) {
      return next.handle(request);
    }

    // Skip non-API requests (assets, i18n, etc.)
    if (!request.url.includes('/api/')) {
      return next.handle(request);
    }

    // Route to appropriate mock handler
    const mockResponse = this.handleRequest(request);

    if (mockResponse !== null) {
      return of(new HttpResponse({ status: 200, body: mockResponse })).pipe(
        delay(this.mockDelay)
      );
    }

    // If no mock handler, pass through to real backend (will fail if no backend)
    return next.handle(request);
  }

  private handleRequest(request: HttpRequest<unknown>): unknown {
    const url = request.url;
    const method = request.method;

    const requestResult = this.handleRequestEndpoints(url, method, request);
    if (requestResult !== undefined) {
      return requestResult;
    }

    const companyResult = this.handleCompanyEndpoints(url, method, request);
    if (companyResult !== undefined) {
      return companyResult;
    }

    const userResult = this.handleUserEndpoints(url, method, request);
    if (userResult !== undefined) {
      return userResult;
    }

    const referenceResult = this.handleReferenceEndpoints(url);
    if (referenceResult !== undefined) {
      return referenceResult;
    }

    return null;
  }

  private handleRequestEndpoints(
    url: string,
    method: string,
    request: HttpRequest<unknown>
  ): unknown {
    if (url.includes('/api/v1/request/filter-options')) {
      return this.getRequestFilterOptions();
    }

    if (/\/api\/v1\/request\/\d+$/.exec(url) && method === 'GET') {
      const requestNumber = this.extractIdFromUrl(url);
      return getMockRequestDetail(parseInt(requestNumber, 10));
    }

    if (url.includes('/api/v1/request') && method === 'GET') {
      return this.getPagedRequests(request);
    }

    return undefined;
  }

  private handleCompanyEndpoints(
    url: string,
    method: string,
    request: HttpRequest<unknown>
  ): unknown {
    if (url.includes('/api/v1/company/salesforce') && method === 'GET') {
      const query = request.params.get('query') ?? '';
      return searchMockCompanies(query, 10, 1);
    }

    if (/\/api\/v1\/company\/[A-Za-z0-9]+$/.exec(url) && method === 'GET') {
      const arNumber = this.extractIdFromUrl(url);
      return getMockCompanyByArNumber(arNumber);
    }

    if (/\/api\/v1\/company\/[A-Za-z0-9]+$/.exec(url) && method === 'PUT') {
      const arNumber = this.extractIdFromUrl(url);
      const company = getMockCompanyByArNumber(arNumber);
      return company ? { ...company, ...(request.body as object) } : null;
    }

    if (url.includes('/api/v1/company') && method === 'POST') {
      return { ...(request.body as object), arNumber: `AR${Date.now()}` };
    }

    if (url.includes('/api/v1/company') && method === 'GET') {
      return this.getPagedCompanies(request);
    }

    return undefined;
  }

  private handleUserEndpoints(
    url: string,
    method: string,
    request: HttpRequest<unknown>
  ): unknown {
    if (url.includes('/api/v1/user') && method === 'GET') {
      return this.getPagedUsers(request);
    }

    return undefined;
  }

  private handleReferenceEndpoints(url: string): unknown {
    if (
      url.includes('/api/v1/reference/print-sites') ||
      url.includes('/api/v1/printsite')
    ) {
      return getMockPrintSites(true);
    }

    if (
      url.includes('/api/v1/reference/check-layouts') ||
      url.includes('/api/v1/checklayout')
    ) {
      return getMockCheckLayouts();
    }

    if (
      url.includes('/api/v1/reference/customer-types') ||
      url.includes('/api/v1/customertype')
    ) {
      return this.getMockCustomerTypes();
    }

    if (url.includes('/api/v1/role')) {
      return this.getMockRoles();
    }

    return undefined;
  }

  private getPagedRequests(request: HttpRequest<unknown>): unknown {
    const page = parseInt(request.params.get('page') ?? '1', 10);
    const pageSize = parseInt(request.params.get('pageSize') ?? '25', 10);
    const search = request.params.get('search') ?? '';
    const status = request.params.get('status') ?? '';
    const isExpedited = request.params.get('isExpedited');
    const isPrinted = request.params.get('isPrinted');
    const country = request.params.get('country') ?? '';
    const sortBy = request.params.get('sortBy') ?? 'requestDate';
    const sortDescending = request.params.get('sortDescending') === 'true';

    let requests = getMockRequests();

    // Apply filters
    if (search) {
      const searchLower = search.toLowerCase();
      requests = requests.filter(
        r =>
          r.requestNumber.toLowerCase().includes(searchLower) ||
          r.arNumber.toLowerCase().includes(searchLower) ||
          r.createdBy.toLowerCase().includes(searchLower)
      );
    }

    if (status) {
      requests = requests.filter(r => r.statusDisplay === status);
    }

    if (isExpedited !== null && isExpedited !== '') {
      requests = requests.filter(
        r => r.isExpedited === (isExpedited === 'true')
      );
    }

    if (isPrinted !== null && isPrinted !== '') {
      requests = requests.filter(r => r.isPrinted === (isPrinted === 'true'));
    }

    if (country) {
      requests = requests.filter(r => r.country === country);
    }

    // Sort
    requests = this.sortArray(requests, sortBy, sortDescending);

    // Paginate
    const totalCount = requests.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (page - 1) * pageSize;
    const items = requests.slice(startIndex, startIndex + pageSize);

    // Return in BackendPagedResult format (uses 'page' not 'pageNumber')
    return {
      items,
      totalCount,
      page, // Backend uses 'page', repository transforms to 'pageNumber'
      pageSize,
      totalPages,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
    };
  }

  private getPagedCompanies(request: HttpRequest<unknown>) {
    const page = parseInt(request.params.get('page') ?? '1', 10);
    const pageSize = parseInt(request.params.get('pageSize') ?? '10', 10);
    return getPaginatedMockCompanies(pageSize, page);
  }

  private getPagedUsers(request: HttpRequest<unknown>) {
    const page = parseInt(request.params.get('page') ?? '1', 10);
    const pageSize = parseInt(request.params.get('pageSize') ?? '25', 10);
    const search = request.params.get('search') ?? '';
    const status = request.params.get('status');
    const roleId = request.params.get('roleId');

    let users = getMockUsers();

    // Apply filters
    if (search) {
      const searchLower = search.toLowerCase();
      users = users.filter(
        u =>
          u.userName.toLowerCase().includes(searchLower) ||
          u.fullName.toLowerCase().includes(searchLower) ||
          (u.email ?? '').toLowerCase().includes(searchLower)
      );
    }

    if (status === 'active') {
      users = users.filter(u => u.active);
    } else if (status === 'inactive') {
      users = users.filter(u => !u.active);
    }

    if (roleId) {
      const roleName = this.getMockRoles().find(
        r => r.id.toString() === roleId
      )?.name;
      if (roleName) {
        users = users.filter(u => u.roleName === roleName);
      }
    }

    // Paginate
    const totalCount = users.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (page - 1) * pageSize;
    const items = users.slice(startIndex, startIndex + pageSize);

    // Return in BackendPagedResult format (uses 'page' not 'pageNumber')
    return {
      items,
      totalCount,
      page, // Backend uses 'page', repository transforms to 'pageNumber'
      pageSize,
      totalPages,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
    };
  }

  private getRequestFilterOptions() {
    const requests = getMockRequests();
    const countries = [
      ...new Set(requests.map(r => r.country).filter(Boolean)),
    ];
    const printSites = [
      ...new Set(requests.map(r => r.printSite).filter(Boolean)),
    ];
    const statuses = [
      ...new Set(requests.map(r => r.statusDisplay).filter(Boolean)),
    ];

    return { countries, printSites, statuses };
  }

  private getMockCustomerTypes() {
    return [
      { id: 1, name: 'Enterprise', isActive: true },
      { id: 2, name: 'SMB', isActive: true },
      { id: 3, name: 'Government', isActive: true },
      { id: 4, name: 'Non-Profit', isActive: true },
      { id: 5, name: 'Startup', isActive: true },
    ];
  }

  private getMockRoles() {
    return [
      { id: 1, name: 'Admin' },
      { id: 2, name: 'User' },
      { id: 3, name: 'Viewer' },
      { id: 4, name: 'Manager' },
      { id: 5, name: 'Operator' },
    ];
  }

  private sortArray<T>(array: T[], field: string, descending: boolean): T[] {
    return [...array].sort((a, b) =>
      this.compareValues(
        (a as Record<string, unknown>)[field],
        (b as Record<string, unknown>)[field],
        descending
      )
    );
  }

  private compareValues(
    aVal: unknown,
    bVal: unknown,
    descending: boolean
  ): number {
    const result = this.getComparisonResult(aVal, bVal);
    return descending ? -result : result;
  }

  private getComparisonResult(aVal: unknown, bVal: unknown): number {
    if (aVal === null || aVal === undefined) return -1;
    if (bVal === null || bVal === undefined) return 1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal);
    }

    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  }

  private extractIdFromUrl(url: string): string {
    const segments = url.split('/');
    return segments[segments.length - 1].split('?')[0];
  }
}
